// Rest timer sound catalog. The audio file bytes themselves live in
// the per-platform asset trees (apps/mobile/android/app/src/main/res/raw/
// and apps/web/src/assets/sounds/) because RN and Vite have
// incompatible asset bundling. Manifest entries point at logical IDs;
// the loader in each app maps id -> bundled file.
//
// `'none'` is a first-class entry — picking it silences the cue while
// keeping the rest of the timer (vibration, banner countdown) intact.
// All CC0 from freesound.org; see CREDITS.md next to the audio files.
//
// Add a sound: drop the MP3 in both asset trees, add a row here, add
// `settings.audio.soundOptions.<id>` to en.json, register it in each
// platform's loader (mobile: Sound() constructor, web: import path).

export type RestSoundCategory = 'serious' | 'fun';

export type RestSoundId =
  | 'none'
  | 'chime_crisp'
  | 'ding_dong'
  | 'meditation_bell'
  | 'tada_fanfare'
  | 'cartoon_boing'
  | 'ah_yeah';

export type RestSound = {
  id: RestSoundId;
  category: RestSoundCategory | null; // null only for 'none'
  durationMs: number; // 0 for 'none'
};

export const REST_SOUNDS: readonly RestSound[] = [
  { id: 'none', category: null, durationMs: 0 },
  { id: 'chime_crisp', category: 'serious', durationMs: 600 },
  { id: 'ding_dong', category: 'serious', durationMs: 800 },
  { id: 'meditation_bell', category: 'serious', durationMs: 2500 },
  { id: 'tada_fanfare', category: 'fun', durationMs: 1690 },
  { id: 'cartoon_boing', category: 'fun', durationMs: 2250 },
  { id: 'ah_yeah', category: 'fun', durationMs: 1410 },
];

export const DEFAULT_REST_SOUND_ID: RestSoundId = 'chime_crisp';

const ID_SET = new Set<RestSoundId>(REST_SOUNDS.map((s) => s.id));

export function isRestSoundId(value: unknown): value is RestSoundId {
  return typeof value === 'string' && ID_SET.has(value as RestSoundId);
}

export function restSoundLabelKey(id: RestSoundId): string {
  return `settings.audio.soundOptions.${id}`;
}

// Android res/raw resource name for a sound. Both the notifee channel and
// the react-native-sound preview resolve `jl_<id>`. Returns null for
// 'none' (silent → no resource); callers coerce to their own empty value.
export function restSoundRawName(id: RestSoundId): string | null {
  return id === 'none' ? null : `jl_${id}`;
}
